import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from "react-redux";
import styles from './Create.module.scss';
import classNames from "classnames/bind";
import { createExam, getSubject } from '../../../redux/apiRequest';

const cx = classNames.bind(styles);

function CreateExam({ onClose, user, axiosJWT }) {
    const dispatch = useDispatch();
    const [selectedSubjects, setSelectedSubjects] = useState([{ subjectId: '', numQuestions: 0 }]);
    const [numExams, setNumExams] = useState(1);
    const [formErrors, setFormErrors] = useState({
        selectedSubjects: '',
        numExams: '',
    });

    const state = useSelector((state) => state.subjects.subjects);
    const subjects = state?.listsubjects || [];

    useEffect(() => {
        if (user?.accessToken) {
            getSubject(user.accessToken, dispatch, axiosJWT);
        }
    }, [user?.accessToken, dispatch, axiosJWT]);

    const totalQuestions = selectedSubjects.reduce(
        (total, subject) => total + (Number(subject.numQuestions) || 0),
        0,
    );

    const successCallback = async () => {
        await onClose(false);
    };

    const errCallback = async () => {
        await onClose(false);
    };

    const validateForm = () => {
        const errors = {
            selectedSubjects: '',
            numExams: '',
        };

        let hasErrors = false;
        const hasValidSubject = selectedSubjects.some(
            (subject) => subject.subjectId && Number(subject.numQuestions) > 0,
        );

        if (!hasValidSubject) {
            errors.selectedSubjects = 'Vui lòng chọn ít nhất một chuyên đề và số câu hỏi hợp lệ.';
            hasErrors = true;
        }

        if (!numExams || Number(numExams) <= 0) {
            errors.numExams = 'Số lượng đề thi phải lớn hơn 0.';
            hasErrors = true;
        }

        setFormErrors(errors);
        return !hasErrors;
    };

    const handleAdd = async () => {
        if (!validateForm()) {
            return;
        }

        const payload = {
            selectedSubjects: selectedSubjects.map((subject) => ({
                ...subject,
                numQuestions: Number(subject.numQuestions) || 0,
            })),
            numExams: Number(numExams),
        };

        await createExam(dispatch, user?.accessToken, payload, axiosJWT, successCallback, errCallback);
    };

    const handleSubjectChange = (index, field, value) => {
        const newSelectedSubjects = [...selectedSubjects];
        const current = newSelectedSubjects[index] || { subjectId: '', numQuestions: 0 };

        let nextValue = value;
        if (field === 'numQuestions') {
            const parsedValue = Number(value);
            nextValue = Number.isNaN(parsedValue) ? '' : Math.max(0, parsedValue);
        }

        newSelectedSubjects[index] = {
            ...current,
            [field]: nextValue,
        };

        setSelectedSubjects(newSelectedSubjects);
    };

    const addSubject = () => {
        setSelectedSubjects((prev) => [...prev, { subjectId: '', numQuestions: 0 }]);
    };

    const removeSubject = (index) => {
        setSelectedSubjects((prev) => {
            if (prev.length === 1) {
                return [{ subjectId: '', numQuestions: 0 }];
            }
            return prev.filter((_, i) => i !== index);
        });
    };

    const getAvailableSubjects = (index) => {
        const selectedIds = selectedSubjects
            .map((subject, idx) => (idx === index ? null : subject.subjectId))
            .filter(Boolean)
            .map((id) => String(id));

        return subjects.filter((subject) => !selectedIds.includes(String(subject.id)));
    };

    return (
        <div className={cx('overlay')}>
            <div className={cx('modal')}>
                <button type="button" className={cx('close-button')} onClick={onClose} aria-label="Đóng">
                    ×
                </button>

                <header className={cx('header')}>
                    <span className={cx('header__subtitle')}>Tạo đề thi</span>
                    <h2 className={cx('header__title')}>Cá nhân hóa đề thi từ các chuyên đề</h2>

                </header>

                <section className={cx('metrics')}>
                    <div className={cx('metric-card')}>
                        <span className={cx('metric-card__label')}>Chuyên đề đã chọn</span>
                        <span className={cx('metric-card__value')}>{selectedSubjects.length}</span>
                    </div>
                    <div className={cx('metric-card')}>
                        <span className={cx('metric-card__label')}>Tổng số câu hỏi</span>
                        <span className={cx('metric-card__value')}>{totalQuestions}</span>
                    </div>
                    <div className={cx('metric-card')}>
                        <span className={cx('metric-card__label')}>Số đề dự kiến</span>
                        <span className={cx('metric-card__value')}>{numExams}</span>
                    </div>
                </section>

                <section className={cx('subject-section')}>
                    <div className={cx('subject-section__header')}>
                        <h3>Thiết lập chuyên đề</h3>
                        <button type="button" className={cx('add-subject')} onClick={addSubject}>
                            + Thêm chuyên đề
                        </button>
                    </div>

                    {formErrors.selectedSubjects && (
                        <div className={cx('error-message')}>{formErrors.selectedSubjects}</div>
                    )}

                    <div className={cx('subject-list')}>
                        {selectedSubjects.map((subject, index) => (
                            <div key={index} className={cx('subject-row')}>
                                <div className={cx('field')}>
                                    <label htmlFor={`subject-${index}`}>Chuyên đề</label>
                                    <select
                                        id={`subject-${index}`}
                                        value={subject.subjectId}
                                        onChange={(e) => handleSubjectChange(index, 'subjectId', e.target.value)}
                                    >
                                        <option value="">Chọn chuyên đề</option>
                                        {getAvailableSubjects(index).map((sub) => (
                                            <option key={sub.id} value={sub.id}>
                                                {sub.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className={cx('field')}>
                                    <label htmlFor={`question-${index}`}>Số câu hỏi</label>
                                    <input
                                        id={`question-${index}`}
                                        type="number"
                                        min="0"
                                        value={subject.numQuestions}
                                        onChange={(e) => handleSubjectChange(index, 'numQuestions', e.target.value)}
                                    />
                                </div>
                                <button
                                    type="button"
                                    className={cx('remove-button')}
                                    onClick={() => removeSubject(index)}
                                >
                                    Xóa
                                </button>
                            </div>
                        ))}
                    </div>
                </section>

                <footer className={cx('footer')}>
                    <div className={cx('exam-count')}>
                        <label htmlFor="exam-count-input">Số lượng đề thi</label>
                        <input
                            id="exam-count-input"
                            type="number"
                            min="1"
                            value={numExams}
                            onChange={(e) => setNumExams(Math.max(1, Number(e.target.value) || 1))}
                        />
                        {formErrors.numExams && (
                            <div className={cx('error-message')}>{formErrors.numExams}</div>
                        )}
                    </div>
                    <div className={cx('actions')}>
                        <button type="button" className={cx('secondary-button')} onClick={onClose}>
                            Hủy
                        </button>
                        <button type="button" className={cx('primary-button')} onClick={handleAdd}>
                            Tạo đề
                        </button>
                    </div>
                </footer>
            </div>
        </div>
    );
}

export default CreateExam;
